import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

import ModelAccess from "../ModelForm/ModelAccess";
import ModelsAPI from "../../API/models.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

import BaseModal from "./BaseModal";

const ModelAccessModal = ({
    isVisible,
    setIsVisible,
    modelUserAccess,
    modelOrganizationAccess,
    createdBy,
    data,
    isLoading = false,
    creatorsDictionary,
    creatorsDictionarySetter,
}) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedOrganizations, setSelectedOrganizations] = useState([]);
    const [userAccess, setUserAccess] = useState([]);
    const [organizationAccess, setOrganizationAccess] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mappedUserAccesses, setMappedUserAccesses] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        const mappedAccess = selectedUsers.map((user) => {
            let tempUser;
            if (mappedUserAccesses[user.value.id]?.value === "Read/Write") {
                tempUser = {
                    userId: user.value.id,
                    read: true,
                    write: true,
                };
            } else {
                tempUser = {
                    userId: user.value.id,
                    read: true,
                    write: false,
                };
            }
            return user.value.isDeleted
                ? { ...tempUser, isDeleted: true }
                : { ...tempUser, isDeleted: false };
        });
        setUserAccess(mappedAccess);
    }, [selectedUsers]);

    useEffect(() => {
        const mappedAccess = selectedOrganizations.map((organization) => {
            if (organization.isDeleted) {
                return { organizationId: organization.value, isDeleted: true };
            } else {
                return {
                    organizationId: organization.value,
                    read: true,
                    write: false,
                };
            }
        });
        setOrganizationAccess(mappedAccess);
    }, [selectedOrganizations]);

    const closeModal = () => {
        setIsVisible(false);
    };

    const completionHandler = async () => {
        try {
            const result = await ModelsAPI.updateModelUserAccesses({
                modelId: data.id,
                modelUserAccesses: userAccess,
                modelOrganizationAccesses: organizationAccess,
            });
            if (result) {
                SuccessAlert("Access Granted");
                LogCapture.info(
                    `Model user access updated with model id: ${data?.id}`,
                    ENTITIES.models,
                    ACTIONS.models.editAccess
                );
            }
        } catch (error) {
            ErrorAlert(error);
            LogCapture.error(
                `Couldn't updated model user access with model id: ${data?.id}, error: ${error}`
            );
        }
        closeModal();
        setIsSubmitting(false);
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
        completionHandler();
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                isSubmitting={isSubmitting}
                setIsVisible={setIsVisible}
                titleIcon="fas fa-users-cog"
                title={
                    <>
                        {data
                            ? t("modelAccessHeader")
                            : t("modelAccessHeaderAdd")}
                        <b>{data.modelName}</b>
                    </>
                }
                action={{
                    submitAction: handleSubmit,
                    name: data.id ? t("edit") : t("add"),
                }}
                isLoading={isLoading}
            >
                <ModelAccess
                    modelUserAccesses={modelUserAccess}
                    modelOrganizationAccesses={modelOrganizationAccess}
                    createdBy={createdBy}
                    selectedUsers={selectedUsers}
                    setSelectedUsers={setSelectedUsers}
                    selectedOrganizations={selectedOrganizations}
                    setSelectedOrganizations={setSelectedOrganizations}
                    mappedUserAccesses={mappedUserAccesses}
                    setMappedUserAccesses={setMappedUserAccesses}
                    creatorsDictionary={creatorsDictionary}
                    creatorsDictionarySetter={creatorsDictionarySetter}
                    resetForm={!isVisible}
                />
            </BaseModal>
        </>
    );
};

export default ModelAccessModal;
